import React, {FormEvent, useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {api} from "../api";
import type {PermissionSection, Role} from "../types";
import ActionMenu, {type IMenuAction} from "./ActionMenu";
import Pagination from "./Pagination";
import {readPaginationPreference, writePaginationPreference} from "../paginationPreferences";
import {clearFormDraft, readFormDraft, sameFormValue, writeFormDraft} from "../formDrafts";
import useDialogFocus from "./useDialogFocus";

type Props = {permissions: string[]};
type RoleForm = {code: string; name: string; description: string; permissions: string[]};
const emptyForm: RoleForm = {code: "", name: "", description: "", permissions: []};
const actionOrder = ["read", "create", "update", "delete"];
const paginationKey = "roles";
const roleDraftKey = (role?: Role | null) => `admin-id-express-form-draft:role:${role?.id || "create"}`;

export default function RolesPanel({permissions}: Props) {
  const {t} = useTranslation();
  const initialPagination = useMemo(() => readPaginationPreference(paginationKey), []);
  const [roles, setRoles] = useState<Role[]>([]);
  const [sections, setSections] = useState<PermissionSection[]>([]);
  const [pagination, setPagination] = useState({currentPage: initialPagination.page, totalPages: 0, count: 0});
  const [itemsPerPage, setItemsPerPage] = useState(initialPagination.pageSize);
  const [editing, setEditing] = useState<Role | null | undefined>(undefined);
  const [removing, setRemoving] = useState<Role | null>(null);
  const [form, setForm] = useState<RoleForm>(emptyForm);
  const [formBaseline, setFormBaseline] = useState<RoleForm>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pageError, setPageError] = useState("");
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const can = (permission: string) => permissions.includes("*") || permissions.includes(permission);

  const load = useCallback(async (currentPage = 1, nextItemsPerPage = itemsPerPage) => {
    setItemsPerPage(nextItemsPerPage);
    writePaginationPreference(paginationKey, {page: currentPage, pageSize: nextItemsPerPage});
    setLoading(true); setPageError("");
    try {
      const response = await api.get("/roles", {params: {current_page: currentPage, items_per_page: nextItemsPerPage}});
      setRoles(response.data.data || []);
      setSections(response.data.permissionSections || []);
      setPagination({
        currentPage: Number(response.data.current_page || currentPage),
        totalPages: Number(response.data.total_pages || 0),
        count: Number(response.data.count || 0),
      });
    } catch (requestError: any) {
      setPageError(requestError.response?.status === 403 ? t("rolesPanel.denied") : t("rolesPanel.genericError"));
    } finally { setLoading(false); }
  }, [itemsPerPage, t]);

  useEffect(() => { load(initialPagination.page, initialPagination.pageSize); }, []);

  const formDirty = useMemo(() => !sameFormValue(form, formBaseline), [form, formBaseline]);
  const currentDraftKey = editing !== undefined ? roleDraftKey(editing) : "";

  useEffect(() => {
    if (editing === undefined || !currentDraftKey) return;
    if (formDirty) writeFormDraft(currentDraftKey, form);
    else clearFormDraft(currentDraftKey);
  }, [currentDraftKey, editing, form, formDirty]);

  const openCreate = () => {
    setFormBaseline(emptyForm);
    setForm(readFormDraft(roleDraftKey(null), emptyForm));
    setEditing(null); setFormError(""); setPageError(""); setNotice("");
  };
  const openEdit = (role: Role) => {
    const baseline = {code: role.code, name: role.name, description: role.description || "", permissions: [...role.permissions]};
    setFormBaseline(baseline);
    setForm(readFormDraft(roleDraftKey(role), baseline));
    setEditing(role); setFormError(""); setPageError(""); setNotice("");
  };
  const close = useCallback(() => {
    if (busy) return;
    if (formDirty && !window.confirm(t("forms.closeDirtyConfirm"))) return;
    setEditing(undefined);
  }, [busy, formDirty, t]);
  const editorDialogRef = useDialogFocus<HTMLDivElement>(editing !== undefined, close);
  const removeDialogRef = useDialogFocus<HTMLDivElement>(Boolean(removing), () => { if (!busy) setRemoving(null); });
  useEffect(() => {
    if (editing === undefined) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!formDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [close, editing, formDirty]);
  const sectionCodes = (section: PermissionSection) => [section.viewPermission, ...section.permissions.map(item => item.code)];
  const actionColumns = useMemo(() => {
    const actions = Array.from(new Set(sections.flatMap(section => section.permissions.map(item => item.action))));
    return actions.sort((left, right) => {
      const leftIndex = actionOrder.indexOf(left);
      const rightIndex = actionOrder.indexOf(right);
      if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
      if (leftIndex === -1) return 1;
      if (rightIndex === -1) return -1;
      return leftIndex - rightIndex;
    });
  }, [sections]);
  const actionCodes = (section: PermissionSection, action: string) => section.permissions.filter(item => item.action === action).map(item => item.code);
  const allKnownCodes = useMemo(() => sections.flatMap(sectionCodes), [sections]);
  const allActionCodes = (action: string) => sections.flatMap(section => actionCodes(section, action));
  const hasEvery = (codes: string[]) => codes.length > 0 && codes.every(code => form.permissions.includes(code));
  const toggleSection = (section: PermissionSection) => setForm(current => {
    const enabled = current.permissions.includes(section.viewPermission);
    const codes = new Set(current.permissions);
    if (enabled) sectionCodes(section).forEach(code => codes.delete(code));
    else codes.add(section.viewPermission);
    return {...current, permissions: Array.from(codes)};
  });
  const toggleAllSections = () => setForm(current => {
    const codes = new Set(current.permissions);
    const allSelected = sections.length > 0 && sections.every(section => codes.has(section.viewPermission));
    sections.forEach(section => { if (allSelected) codes.delete(section.viewPermission); else codes.add(section.viewPermission); });
    return {...current, permissions: Array.from(codes)};
  });
  const toggleWholeSection = (section: PermissionSection) => setForm(current => {
    const codes = new Set(current.permissions);
    const sectionSelected = sectionCodes(section).every(code => codes.has(code));
    sectionCodes(section).forEach(code => { if (sectionSelected) codes.delete(code); else codes.add(code); });
    return {...current, permissions: Array.from(codes)};
  });
  const toggleActionColumn = (action: string) => setForm(current => {
    const codes = new Set(current.permissions);
    const targetCodes = allActionCodes(action);
    const selected = targetCodes.length > 0 && targetCodes.every(code => codes.has(code));
    sections.forEach(section => {
      const sectionActionCodes = actionCodes(section, action);
      if (!sectionActionCodes.length) return;
      if (selected) sectionActionCodes.forEach(code => codes.delete(code));
      else { codes.add(section.viewPermission); sectionActionCodes.forEach(code => codes.add(code)); }
    });
    return {...current, permissions: Array.from(codes)};
  });
  const toggleSectionAction = (section: PermissionSection, action: string) => setForm(current => {
    const codes = new Set(current.permissions);
    const targetCodes = actionCodes(section, action);
    const selected = targetCodes.length > 0 && targetCodes.every(code => codes.has(code));
    if (selected) targetCodes.forEach(code => codes.delete(code));
    else { codes.add(section.viewPermission); targetCodes.forEach(code => codes.add(code)); }
    return {...current, permissions: Array.from(codes)};
  });
  const selectEverything = () => setForm(current => ({...current, permissions: Array.from(new Set([...current.permissions, ...allKnownCodes]))}));
  const clearEverything = () => setForm(current => {
    const known = new Set(allKnownCodes);
    return {...current, permissions: current.permissions.filter(code => !known.has(code))};
  });
  const rowActions = (role: Role) => {
    const actions: IMenuAction[] = [];
    if (can("admin.roles.update") && role.code !== "superadmin") actions.push({id: "edit", label: t("rolesPanel.edit"), icon: "pencil"});
    if (can("admin.roles.delete") && !role.system) {
      if (actions.length) actions.push({type: "divider"});
      actions.push({id: "delete", label: t("rolesPanel.delete"), icon: "trash", isDanger: true});
    }
    return actions;
  };
  const onRowAction = (actionId: string, record: Record<string, any>) => {
    const role = roles.find(item => item.id === record.id);
    if (!role) return;
    if (actionId === "edit") openEdit(role);
    if (actionId === "delete") setRemoving(role);
  };

  const localError = (requestError: any) => {
    const code = String(requestError.response?.data?.code || "");
    if (requestError.response?.status === 403 || code === "permission_escalation_forbidden") return t("rolesPanel.escalationDenied");
    if (code === "role_exists") return t("rolesPanel.duplicate");
    return t("rolesPanel.genericError");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setFormError(""); setNotice("");
    try {
      const payload = {name: form.name.trim(), description: form.description.trim(), permissions: form.permissions};
      if (editing) {
        await api.put(`/roles/${editing.id}`, payload);
        setNotice(t("rolesPanel.updated"));
      } else {
        await api.post("/roles", {...payload, code: form.code.trim().toLowerCase()});
        setNotice(t("rolesPanel.created"));
      }
      clearFormDraft(currentDraftKey);
      setEditing(undefined); await load(pagination.currentPage);
    } catch (requestError: any) { setFormError(localError(requestError)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!removing) return;
    setBusy(true); setPageError("");
    try {
      await api.delete(`/roles/${removing.id}`);
      setRemoving(null); setNotice(t("rolesPanel.deleted"));
      await load(roles.length === 1 && pagination.currentPage > 1 ? pagination.currentPage - 1 : pagination.currentPage);
    } catch (requestError: any) { setPageError(localError(requestError)); setRemoving(null); }
    finally { setBusy(false); }
  };

  const permissionCount = useMemo(() => form.permissions.length, [form.permissions]);
  return <div className="roles-panel">
    <div className="section-heading"><div><span className="eyebrow">{t("rbac")}</span><h2>{t("rolesPanel.title")}</h2><p>{t("rolesPanel.subtitle")}</p></div>
      {can("admin.roles.create") && <button type="button" className="primary action-primary" title={t("rolesPanel.add")} onClick={openCreate}>＋ {t("rolesPanel.add")}</button>}
    </div>
    {notice && <div className="notice" role="status">✓ {notice}</div>}
    {pageError && editing === undefined && <div className="alert" role="alert">{pageError}</div>}
    {loading ? <div className="empty compact"><span className="mini-spinner"/><p>{t("rolesPanel.loading")}</p></div> :
      <div className="table-shell roles-table"><table><thead><tr><th className="action-menu-column">{t("administrators.actions")}</th><th>{t("rolesPanel.name")}</th><th>{t("rolesPanel.code")}</th><th>{t("rolesPanel.permissions")}</th><th>{t("rolesPanel.type")}</th></tr></thead>
        <tbody>{roles.map(role => <tr key={role.id}><td className="action-menu-cell"><ActionMenu record={role as unknown as Record<string, any>} actions={rowActions(role)} onAction={onRowAction} triggerLabel={`${t("administrators.actions")}: ${role.name}`}/></td><td><b>{role.name}</b><small className="role-description">{role.description || "—"}</small></td><td><code>{role.code}</code></td><td>{role.permissions.length}</td><td><span className="role-kind">{role.system ? t("rolesPanel.system") : t("rolesPanel.custom")}</span></td></tr>)}</tbody></table></div>}
    {!loading && pagination.count > 0 && <Pagination {...pagination} pageSize={itemsPerPage} disabled={busy} onPageChange={page => load(page)} onPageSizeChange={pageSize => load(1, pageSize)}/>}

    {editing !== undefined && <div className="modal-backdrop" role="presentation" onMouseDown={event => {if (event.target === event.currentTarget) close();}}><div ref={editorDialogRef} tabIndex={-1} className="modal role-editor" role="dialog" aria-modal="true" aria-labelledby="role-editor-title">
      <button type="button" className="modal-close" title={t("administrators.close")} aria-label={t("administrators.close")} onClick={close}>×</button><span className="eyebrow">{t("rbac")}</span><h2 id="role-editor-title">{editing ? t("rolesPanel.edit") : t("rolesPanel.add")}</h2><p>{t("rolesPanel.help")}</p>
      <form onSubmit={submit}><div className="form-grid"><label>{t("rolesPanel.code")}<input required pattern="[a-z][a-z0-9_.-]{1,99}" value={form.code} disabled={Boolean(editing)} onChange={event => setForm({...form, code: event.target.value})}/></label><label>{t("rolesPanel.name")}<input required maxLength={200} value={form.name} onChange={event => setForm({...form, name: event.target.value})}/></label><label className="full">{t("rolesPanel.description")}<input value={form.description} onChange={event => setForm({...form, description: event.target.value})}/></label></div>
        <fieldset><legend>{t("rolesPanel.sectionAccess")} <small>{permissionCount} {t("rolesPanel.selected")}</small></legend><p>{t("rolesPanel.sectionHelp")}</p><div className="permission-bulk-actions"><button type="button" className="secondary" title={t("rolesPanel.selectEverything", {defaultValue: "Select all permissions"})} onClick={selectEverything}>{t("rolesPanel.selectEverything", {defaultValue: "Select all"})}</button><button type="button" className="secondary" title={t("rolesPanel.clearEverything", {defaultValue: "Clear all permissions"})} onClick={clearEverything}>{t("rolesPanel.clearEverything", {defaultValue: "Clear all"})}</button></div><div className="permission-table-shell"><table className="permission-table"><thead><tr><th>{t("rolesPanel.section", {defaultValue: "Section"})}</th><th><span>{t("rolesPanel.allSectionPermissions", {defaultValue: "All"})}</span></th><th><label title={t("rolesPanel.toggleAllSections", {defaultValue: "Toggle viewing for all sections"})}><input type="checkbox" checked={sections.length > 0 && sections.every(section => form.permissions.includes(section.viewPermission))} onChange={toggleAllSections}/>{t("rolesPanel.canView")}</label></th>{actionColumns.map(action => <th key={action}><label title={t("rolesPanel.toggleActionColumn", {action: t(`rolesPanel.actions.${action}`, {defaultValue: action}), defaultValue: `Toggle all ${action}`})}><input type="checkbox" checked={hasEvery(allActionCodes(action))} onChange={() => toggleActionColumn(action)}/>{t(`rolesPanel.actions.${action}`, {defaultValue: action})}</label></th>)}</tr></thead><tbody>{sections.map(section => {const visible = form.permissions.includes(section.viewPermission); const wholeSectionSelected = sectionCodes(section).every(code => form.permissions.includes(code)); return <tr key={section.key} className={visible ? "is-visible" : ""}><td><b>{t(`rolesPanel.sections.${section.key}`, {defaultValue: section.label})}</b><small>{section.key}</small></td><td><label className="permission-check permission-check-icon" title={t("rolesPanel.allSectionPermissions", {defaultValue: "All"})}><input type="checkbox" aria-label={t("rolesPanel.allSectionPermissions", {defaultValue: "All"})} checked={wholeSectionSelected} onChange={() => toggleWholeSection(section)}/></label></td><td><label className="permission-check permission-check-icon" title={t("rolesPanel.canView")}><input type="checkbox" aria-label={t("rolesPanel.canView")} checked={visible} onChange={() => toggleSection(section)}/></label></td>{actionColumns.map(action => {const codes = actionCodes(section, action); const checked = hasEvery(codes); const label = t(`rolesPanel.actions.${action}`, {defaultValue: action}); return <td key={action}>{codes.length ? <label className="permission-check permission-check-icon" title={label}><input type="checkbox" aria-label={label} checked={checked} disabled={!visible} onChange={() => toggleSectionAction(section, action)}/></label> : <span className="permission-empty">—</span>}</td>;})}</tr>;})}</tbody></table></div></fieldset>
        {formError && <div className="alert form-alert" role="alert">{formError}</div>}<div className="modal-actions"><button type="button" className="secondary" title={t("administrators.cancel")} onClick={close} disabled={busy}>{t("administrators.cancel")}</button><button type="submit" className="primary" title={t("administrators.save")} disabled={busy}>{busy ? t("administrators.saving") : t("administrators.save")}</button></div>
      </form></div></div>}
    {removing && <div className="modal-backdrop" role="presentation"><div ref={removeDialogRef} tabIndex={-1} className="modal confirm-modal" role="alertdialog" aria-modal="true"><div className="danger-symbol">!</div><h2>{t("rolesPanel.confirmTitle")}</h2><p>{t("rolesPanel.confirmText")}</p><div className="delete-target"><b>{removing.name}</b><small>{removing.code}</small></div><div className="modal-actions"><button type="button" className="secondary" title={t("administrators.cancel")} onClick={() => setRemoving(null)} disabled={busy}>{t("administrators.cancel")}</button><button type="button" className="danger-button" title={t("rolesPanel.delete")} onClick={remove} disabled={busy}>{t("rolesPanel.delete")}</button></div></div></div>}
  </div>;
}
